import type { BlockField } from '@/blocks/domain/types';
import type { BlockContent } from '@/blocks/domain/types/BlockContent';
import type { CustomContent } from '@/blocks/domain/types/BlockContent/CustomContent';
import type { CustomerBlockContent } from '@/blocks/domain/types/BlockContent/CustomerBlockContent';
import type { HeaderContent, HeaderSection, SectionLayout } from '@/blocks/domain/types/BlockContent/HeaderContent';
import type { SignatureContent } from '@/blocks/domain/types/BlockContent/SignatureContent';
import { customUsesSections, defaultCustomContent, promoteLegacyCustom } from './customSections';
import { canonicalizeHeader, headerHasVisibleSection, normalizeHeaderSections } from './headerSections';
import { isPartyHeading } from './partyFieldGroups';
import { SIGNATURE_LINES_SECTION_ID } from './signatureDefaults';

/** Stable id so opening the editor twice builds the same tree (the draft stays clean). */
const LEGACY_SECTION_ID = 'sec-legacy-columns';

/**
 * Editor-only promotion of a legacy flat letterhead or customer block into the
 * section model. Already-authored sections are kept. The result is what the block
 * editor holds in memory; it is persisted only when the author saves. Template
 * import normalisers must not call this — a copied instance stays as imported.
 */
export function materializeEditorSections(content: BlockContent): BlockContent {
  if (content.kind === 'header') return materializeHeader(stripInlineImages(content));
  if (content.kind === 'customer_block') return materializeCustomer(stripInlineImages(content));
  if (content.kind === 'signature') return materializeSignature(content);
  if (content.kind === 'custom') return materializeCustom(content);
  return content;
}

/**
 * A Custom block is edited on the section pipeline. Legacy `{ title, bodyHtml }` is promoted to a
 * Heading + Text section (both texts kept); a blank one starts from the default Heading + Text.
 */
function materializeCustom(content: CustomContent): CustomContent {
  if (!customUsesSections(content)) {
    return content.title.trim() !== '' || content.bodyHtml.trim() !== '' ? promoteLegacyCustom(content) : defaultCustomContent();
  }
  const normalized = normalizeHeaderSections({ kind: 'header', fields: content.fields ?? [], sections: content.sections });
  return stripInlineImages({ ...content, sections: normalized.sections, fields: normalized.fields });
}

/**
 * A signature block is edited on the section pipeline (LLAMS-699). Authored sections are kept; a
 * legacy flat one (lines only, `column` or not) becomes one 2-column section, each line in its stored
 * column or alternating left / right - what the flat half-width flow showed. Persisted only on Save.
 */
function materializeSignature(content: SignatureContent): SignatureContent {
  const clean = stripInlineImages(content);
  if (content.sections && content.sections.length > 0) {
    const normalized = normalizeHeaderSections({ kind: 'header', fields: clean.fields, sections: content.sections });
    return { ...clean, sections: normalized.sections, fields: normalized.fields };
  }
  if (clean.fields.length === 0) return clean;

  const sorted = [...clean.fields].sort((a, b) => a.sortOrder - b.sortOrder);
  const stamped = sorted.map((field, index) => ({
    ...field,
    sectionId: SIGNATURE_LINES_SECTION_ID,
    column: typeof field.column === 'number' ? Math.min(Math.max(field.column, 0), 1) : index % 2
  }));
  const canon = canonicalizeHeader([{ id: SIGNATURE_LINES_SECTION_ID, layout: '2' }], stamped);
  return { ...clean, sections: canon.sections, fields: canon.fields };
}

function materializeHeader(content: HeaderContent): HeaderContent {
  if (content.sections && content.sections.length > 0) {
    const normalized = normalizeHeaderSections(content);
    return { ...content, columns: undefined, sections: normalized.sections, fields: normalized.fields };
  }

  const normalized = normalizeHeaderSections(content);
  const promoted: HeaderContent = { ...content, columns: undefined, sections: normalized.sections, fields: normalized.fields };
  if (headerHasVisibleSection(promoted)) return promoted;
  return promoteFlatHeader(content);
}

function promoteFlatHeader(content: HeaderContent): HeaderContent {
  const sorted = [...content.fields].sort((a, b) => a.sortOrder - b.sortOrder);
  if (sorted.length < 2) return content;

  const halves = sorted.filter((field) => field.width === 'half');
  if (halves.length === 0) {
    return stampSection(
      content,
      sorted.map((field) => ({ ...field, column: 0 })),
      '1'
    );
  }

  const fulls = sorted.filter((field) => field.width !== 'half');
  const firstHalf = halves[0];
  if (!firstHalf) return content;
  const columnZero = [...fulls, firstHalf].sort((a, b) => a.sortOrder - b.sortOrder);
  const columnOne = halves.slice(1);
  return stampSection(
    content,
    [...columnZero.map((field) => ({ ...field, column: 0 })), ...columnOne.map((field) => ({ ...field, column: 1 }))],
    '2'
  );
}

function materializeCustomer(content: CustomerBlockContent): CustomerBlockContent {
  const asHeader: HeaderContent = {
    kind: 'header',
    fields: content.fields,
    sections: content.sections,
    columns: content.columns
  };

  if (content.sections && content.sections.length > 0) {
    const normalized = normalizeHeaderSections(asHeader);
    return { ...content, columns: undefined, sections: normalized.sections, fields: normalized.fields };
  }

  if (content.fields.length < 2) return content;

  const stamped = partyColumns(content.fields).map((field) => ({ ...field, sectionId: LEGACY_SECTION_ID }));
  const canon = canonicalizeHeader([{ id: LEGACY_SECTION_ID, layout: '2' }], stamped);
  return { ...content, columns: undefined, sections: canon.sections, fields: canon.fields };
}

function partyColumns(fields: BlockField[]): BlockField[] {
  let column = 0;
  let headings = 0;
  return [...fields]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((field) => {
      if (isPartyHeading(field)) {
        headings += 1;
        if (headings > 1) column = 1;
      }
      return { ...field, column };
    });
}

function stampSection(content: HeaderContent, fields: BlockField[], layout: SectionLayout): HeaderContent {
  const section: HeaderSection = { id: LEGACY_SECTION_ID, layout };
  const canon = canonicalizeHeader(
    [section],
    fields.map((field) => ({ ...field, sectionId: section.id }))
  );
  return { ...content, columns: undefined, sections: canon.sections, fields: canon.fields };
}

function stripInlineImages<T extends { fields: BlockField[] }>(content: T): T {
  return { ...content, fields: content.fields.map(withoutInlineImage) };
}

/** A data URL in an image field inflates the draft PUT. Preview bytes never belong in block JSON. */
function withoutInlineImage(field: BlockField): BlockField {
  if (field.style !== 'image' && field.style !== 'logo') return field;
  if (!field.value?.startsWith('data:')) return field;
  return { ...field, value: '' };
}
